import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { useLocalStorage } from "@/hooks/use-local-storage";

const ONBOARDING_STORAGE_KEY = "onboarding-completed";

interface OnboardingContextType {
  isOnboardingComplete: boolean;
  completeOnboarding: () => void;
  openOnboarding: () => void;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isOnboardingComplete, setIsOnboardingComplete] = useLocalStorage<boolean>(ONBOARDING_STORAGE_KEY, false);
  const [isModalOpen, setIsModalOpen] = useState(!isOnboardingComplete);
  const [showDashboard, setShowDashboard] = useState(isOnboardingComplete);

  // Load dashboard slightly after modal to avoid flash
  useEffect(() => {
    if (!isOnboardingComplete && isModalOpen) {
      const timer = setTimeout(() => {
        setShowDashboard(true);
      }, 100); // Small delay to ensure modal renders first
      return () => clearTimeout(timer);
    } else {
      setShowDashboard(true);
    }
  }, [isOnboardingComplete, isModalOpen]);

  const completeOnboarding = useCallback(() => {
    setIsOnboardingComplete(true);
    setIsModalOpen(false);
  }, [setIsOnboardingComplete]);

  const openOnboarding = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const resetOnboarding = useCallback(() => {
    setIsOnboardingComplete(false);
    setIsModalOpen(true);
  }, [setIsOnboardingComplete]);

  const shouldBlurDashboard = isModalOpen && !isOnboardingComplete;

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingComplete,
        completeOnboarding,
        openOnboarding,
        resetOnboarding,
      }}
    >
      {/* Show dashboard with blur effect when onboarding is in progress */}
      {showDashboard && <div className={shouldBlurDashboard ? "blur-sm pointer-events-none" : ""}>{children}</div>}
      <OnboardingModal open={isModalOpen} onComplete={completeOnboarding} isOnboardingComplete={isOnboardingComplete} />
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};
